import React, { useState, useRef, useCallback, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { Document, Page, pdfjs } from 'react-pdf';
import './PdfMagazine.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const PAGE_RATIO = 297 / 210;
const MOBILE_BP = 768;
// Espace occupé par les 2 flèches + leurs gaps en desktop (2×40px + 2×16px)
const ARROWS_WIDTH = 112;

const PdfPage = React.forwardRef(({ pageNumber, width, height }, ref) => (
  <div ref={ref} className="pdf-magazine-page">
    <Page
      pageNumber={pageNumber}
      width={width}
      height={height}
      renderAnnotationLayer={false}
      renderTextLayer={false}
    />
  </div>
));

function PdfMagazine({ file, downloadUrl }) {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageWidth, setPageWidth] = useState(300);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BP);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const bookRef = useRef();
  const containerRef = useRef();

  const pageHeight = Math.round(pageWidth * PAGE_RATIO);

  const isFirstPage = currentPage === 0;
  const isLastPage  = numPages !== null && currentPage === numPages - 1;

  // Desktop : showCover=true → currentPage saute de 2 (0, 1, 3, 5, …, N-1)
  // Mobile  : usePortrait=true → currentPage s'incrémente de 1 (0, 1, 2, …, N-1)
  const isSinglePage = !isMobile && (isFirstPage || isLastPage);

  // Fenêtre de clipping (desktop uniquement)
  const clipWidth    = isSinglePage && !isTransitioning ? pageWidth : pageWidth * 2;
  const bookOffset   = !isMobile && isFirstPage && !isTransitioning ? -pageWidth : 0;
  const clipOverflow = isTransitioning ? 'visible' : 'hidden';

  // Compteur : numéros de pages PDF réels
  let pageDisplay;
  if (isMobile) {
    pageDisplay = `${currentPage + 1}`;
  } else if (isFirstPage) {
    pageDisplay = '1';
  } else if (isLastPage) {
    pageDisplay = `${numPages}`;
  } else {
    pageDisplay = `${currentPage + 1}–${currentPage + 2}`;
  }

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        if (!entries.length) return;
        const containerWidth = entries[0].contentRect.width;
        const mob = window.innerWidth < MOBILE_BP;
        setIsMobile(mob);
        setCurrentPage(0);
        if (mob) {
          setPageWidth(Math.max(containerWidth, 200));
        } else {
          setPageWidth(Math.min(Math.floor((containerWidth - ARROWS_WIDTH) / 2), 520));
        }
      });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const onDocumentLoad = useCallback(({ numPages }) => setNumPages(numPages), []);

  const onFlip = useCallback((e) => {
    setCurrentPage(e.data);
    setIsTransitioning(false);
  }, []);

  const prevPage = () => {
    setIsTransitioning(true);
    bookRef.current?.pageFlip().flipPrev();
  };

  const nextPage = () => {
    setIsTransitioning(true);
    bookRef.current?.pageFlip().flipNext();
  };

  return (
    <div className="pdf-magazine" ref={containerRef}>
      <Document
        file={file}
        onLoadSuccess={onDocumentLoad}
        loading={<div className="pdf-magazine-loading">Chargement…</div>}
      >
        {numPages && (
          <div className="pdf-flipbook-wrap">
            <button
              className="pdf-flipbook-arrow pdf-flipbook-arrow--prev"
              onClick={prevPage}
              disabled={isFirstPage}
              aria-label="Page précédente"
            >
              <svg width="10" height="16" viewBox="0 0 10 16" fill="none"><path d="M8.5 1L1.5 8L8.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div
              className="pdf-flipbook-clip"
              style={{
                width: isMobile ? pageWidth : clipWidth,
                overflow: isMobile ? 'hidden' : clipOverflow,
                transition: isTransitioning ? 'none' : 'width 0.1s ease',
              }}
            >
              <div style={{ marginLeft: bookOffset }}>
                <HTMLFlipBook
                  key={pageWidth}
                  ref={bookRef}
                  width={pageWidth}
                  height={pageHeight}
                  size="fixed"
                  showCover={!isMobile}
                  mobileScrollSupport={true}
                  onFlip={onFlip}
                  className="pdf-flipbook"
                  flippingTime={600}
                  usePortrait={isMobile}
                  startPage={0}
                  maxShadowOpacity={0.4}
                >
                  {Array.from({ length: numPages }, (_, i) => (
                    <PdfPage key={i} pageNumber={i + 1} width={pageWidth} height={pageHeight} />
                  ))}
                </HTMLFlipBook>
              </div>
            </div>
            <button
              className="pdf-flipbook-arrow pdf-flipbook-arrow--next"
              onClick={nextPage}
              disabled={isLastPage}
              aria-label="Page suivante"
            >
              <svg width="10" height="16" viewBox="0 0 10 16" fill="none"><path d="M1.5 1L8.5 8L1.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        )}
      </Document>

      {numPages && (
        <div className="pdf-magazine-controls">
          <span className="pdf-magazine-counter">{pageDisplay} / {numPages}</span>
          <input
            className="pdf-magazine-slider"
            type="range"
            min={0}
            max={numPages - 1}
            value={currentPage}
            onChange={(e) => bookRef.current?.pageFlip().turnToPage(parseInt(e.target.value))}
            aria-label="Navigation dans le document"
          />
        </div>
      )}

      <a href={downloadUrl} download="RAVAIL-Argumentaire-2026.pdf" className="pdf-magazine-download">
        Télécharger l'argumentaire 2026
      </a>
    </div>
  );
}

export default PdfMagazine;
